import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from 'recharts';

const money = (n) => 'R' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyFull = (n) => 'R' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-ZA', { month: 'short', year: '2-digit' });
};

const TEAL = '#2BA696';
const GOLD = '#C9A227';
const ORANGE = '#D98A3C';
const SLATE = '#7C93A0';

const tooltipStyle = {
  backgroundColor: '#1b2426',
  border: '1px solid #2f3a3d',
  borderRadius: '12px',
  fontSize: '12px',
  color: '#e5eae9',
};
const tooltipItemStyle = { color: '#e5eae9' };
const tooltipLabelStyle = { color: '#9fb0ad' };

const ChartCard = ({ title, testid, children }) => (
  <div data-testid={testid} className="rounded-2xl bg-card border border-border p-5">
    <h3 className="font-heading text-base font-medium text-foreground mb-4">{title}</h3>
    {children}
  </div>
);

export const ReportCharts = ({ byMonth = [], summary = {}, orders = [], bySupplier = [], visible = {} }) => {
  const trendData = byMonth.map(b => ({
    month: monthLabel(b.month),
    'Total Income': b.total_income ?? (b.revenue + b.courier_fee + b.collection_fee),
    'Gross Profit': b.gross_profit,
  }));

  const composition = [
    { name: 'Goods', value: summary.revenue || 0, color: TEAL },
    { name: 'Courier', value: summary.courier_fee || 0, color: ORANGE },
    { name: 'Collection', value: summary.collection_fee || 0, color: GOLD },
  ].filter(d => d.value > 0);

  const payAgg = orders.reduce((acc, o) => {
    const k = o.payment_type === 'Card' ? 'Card' : 'EFT';
    acc[k] = (acc[k] || 0) + (o.total_income || 0);
    return acc;
  }, {});
  const paySplit = [
    { name: 'Card', value: payAgg.Card || 0, color: TEAL },
    { name: 'EFT', value: payAgg.EFT || 0, color: GOLD },
  ].filter(d => d.value > 0);

  const anyVisible = visible.trend || visible.composition || visible.paytype || visible.supplier;
  if (!anyVisible) return null;

  const supplierData = bySupplier.slice(0, 8).map(sup => ({
    name: sup.supplier_name,
    Profit: sup.profit,
    margin: sup.margin,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="report-charts">
      {visible.trend && (
        <ChartCard title="Monthly Income vs Gross Profit" testid="chart-trend">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trendData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2f3a3d" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#9aa7ab', fontSize: 11 }} axisLine={{ stroke: '#2f3a3d' }} tickLine={false} />
              <YAxis tick={{ fill: '#9aa7ab', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={money} width={56} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => moneyFull(v)} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Total Income" fill={GOLD} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Gross Profit" fill={TEAL} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {visible.composition && (
        <ChartCard title="Income Composition" testid="chart-composition">
          {composition.length === 0 ? (
            <p className="font-body text-sm text-foreground/40 text-center py-16">No income for this selection</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={composition} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {composition.map((d, i) => <Cell key={i} fill={d.color} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(v) => moneyFull(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      )}

      {visible.paytype && (
        <ChartCard title="Income by Payment Type" testid="chart-paytype">
          {paySplit.length === 0 ? (
            <p className="font-body text-sm text-foreground/40 text-center py-16">No income for this selection</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={paySplit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} paddingAngle={2}>
                  {paySplit.map((d, i) => <Cell key={i} fill={d.color} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(v) => moneyFull(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      )}

      {visible.supplier && (
        <ChartCard title="Profit by Supplier" testid="chart-supplier">
          {supplierData.length === 0 ? (
            <p className="font-body text-sm text-foreground/40 text-center py-16">No supplier data for this selection</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(260, supplierData.length * 42)}>
              <BarChart layout="vertical" data={supplierData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2f3a3d" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#9aa7ab', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={money} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#9aa7ab', fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  formatter={(v, _n, p) => [`${moneyFull(v)}  (${p.payload.margin}% margin)`, 'Profit']}
                />
                <Bar dataKey="Profit" fill={TEAL} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      )}
    </div>
  );
};
